import type { MentionRow } from './generator';
import { metrics, type MetricId } from './metrics';

/**
 * Rows in, a **weekday × hour** grid out.
 *
 * The third shape in this layer, and it is neither of the other two.
 * `aggregate` always buckets by time and returns a series along one axis;
 * `pivot` never buckets by time at all. A heat grid folds a date range onto two
 * cyclical axes — the same Tuesday 7pm from eight different weeks lands in one
 * cell — so it is a sibling of both rather than an option on either.
 *
 * The properties the other two guarantee hold here too: the cells are reduced
 * over the rows as handed in, `total` reduces the same array the cells were
 * built from, and nothing is invented for a cell nothing landed in — it is a
 * zero, which on this chart means "nothing happened then" and is a reading.
 */

/**
 * Weekday labels in `Date.getDay()` order, which is Sunday-first — and that is
 * a decision about this corpus, not a locale default.
 *
 * The corpus is Bangladeshi telcos: `WEEKDAY` in `generator.ts` puts the
 * weekend on Friday and Saturday because that is the Bangladeshi working week.
 * Starting the grid on Sunday therefore puts the five working days at the top
 * and the two quiet ones at the bottom, which is the order the rows are
 * actually read in. A Monday-first grid would split that weekend across the two
 * ends of the chart.
 */
export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** The 24 columns, midnight first. */
export const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

/**
 * `13` → `1PM`.
 *
 * Spelled out rather than the `1p` this was first written as. The compact form
 * was chosen when the grid had to fit 24 labels across a card; the labels thin
 * to every third now, so there is room for the unambiguous version — and `1p`
 * is a form a reader has to decode rather than read.
 *
 * Also what the card's `Peak window` figure prints, so the grid's axis and the
 * number above it say the hour the same way.
 */
export function hourLabel(hour: number): string {
  if (hour === 0) return '12AM';
  if (hour === 12) return '12PM';
  return hour < 12 ? `${hour}AM` : `${hour - 12}PM`;
}

export type GridCell = { row: number; col: number; value: number };

export type WeekHourGrid = {
  /** `values[row][col]` — the metric over the rows that landed in that cell. */
  values: number[][];
  /** Row totals, in `WEEKDAYS` order — what "strongest day" is read off. */
  rowTotals: number[];
  /** The metric over **all** the rows — the figure a KPI shows. */
  total: number;
  max: number;
  /** The busiest cell, or `null` when nothing was measured at all. */
  peak: GridCell | null;
  /** The rows behind one cell — see the note on the returned closure. */
  rowsAt: (row: number, col: number) => MentionRow[];
};

/**
 * Fold rows onto the weekday × hour grid.
 *
 * Every cell is reduced over its own group rather than accumulated row by row,
 * for the reason `pivot` documents: a non-additive metric (a rate, a mean)
 * becomes a mean of means under a running fold. `mentions` and `engagement` are
 * both additive, so this costs nothing today and is the difference between
 * right and wrong the first time someone passes `engagementRate`.
 *
 * `row.hour` is the local hour and `row.day` is local midnight, so both axes are
 * read in the same timezone the date pickers use — a UTC weekday here would
 * shift the whole grid by a day for part of the corpus.
 */
export function weekHourGrid(rows: readonly MentionRow[], metricId: MetricId): WeekHourGrid {
  const metric = metrics[metricId];

  const groups: MentionRow[][][] = WEEKDAYS.map(() => HOURS.map(() => []));
  for (const row of rows) {
    groups[new Date(row.day).getDay()][row.hour].push(row);
  }

  const values = groups.map((hours) => hours.map((group) => metric.reduce(group)));

  /*
   * Seeded at `value: 0` rather than at `null`, so a grid where nothing was
   * measured falls out as "no peak" without a special case: no cell can beat
   * zero, the seed carries no coordinates, and the check below is the only
   * place emptiness is decided.
   */
  let peak: GridCell = { row: 0, col: 0, value: 0 };
  values.forEach((hours, row) => {
    hours.forEach((value, col) => {
      if (value > peak.value) peak = { row, col, value };
    });
  });

  return {
    values,
    rowTotals: values.map((hours) => hours.reduce((sum, value) => sum + value, 0)),
    total: metric.reduce(rows),
    max: peak.value,
    /* A grid of nothing has no peak to name — an em dash, not cell [0][0]. */
    peak: peak.value > 0 ? peak : null,
    /*
     * The rows behind one cell, for the readout's `View details` — the array the
     * cell's figure was reduced from, handed back rather than re-derived. See
     * `AggregateResult.rowsAt`, which this mirrors for a two-dimensional grid.
     */
    rowsAt: (row: number, col: number) => groups[row]?.[col] ?? [],
  };
}
